import React, { useEffect, useState } from 'react';
import { CardSkeleton } from '../../../components/enterprise';
import {
  Box, Card, CardContent, Stack, Typography, Button, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  Switch, FormControlLabel, Chip, Divider, Alert, CircularProgress,
  Table, TableHead, TableBody, TableRow, TableCell, Tooltip,
  Select, MenuItem, FormControl, InputLabel,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import AddIcon from '@mui/icons-material/Add';
import WarningIcon from '@mui/icons-material/Warning';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import AutoDeleteIcon from '@mui/icons-material/AutoDelete';
import RefreshIcon from '@mui/icons-material/Refresh';
import plansApi from '../../../services/plansApi';
import { useNotification } from '../../../contexts/NotificationContext';

// Default 100 MB/day for new plans. The DB column is still named
// `max_events_per_day` but the value is bytes — this is the daily
// total of raw payload size across all telemetry signals (traces,
// metrics, logs, RUM beacons). Operators edit this in MB through the
// helper UI below.
const DEFAULT_BYTES_PER_DAY = 100 * 1024 * 1024;

const EMPTY_PLAN = {
  code: '', name: '', description: '',
  maxUsers: 5, maxAgents: 3, maxTests: 50, maxEventsPerDay: DEFAULT_BYTES_PER_DAY, maxRumApps: 1,
  retentionRumDays: 7, retentionLogsDays: 7, retentionTracesDays: 7, retentionMetricsDays: 30,
  sessionReplayEnabled: false, advancedFunnelsEnabled: false, ssoEnabled: false, auditLogEnabled: true,
  // Public-signup metadata. signupEnabled is the per-plan flag; the
  // Stripe & Signup operator page has the master flag that gates the
  // whole signup feature.
  signupEnabled: false, displayPrice: '', displayOrder: '', stripePriceId: '', stripeProductId: '',
  // Tier semantics: free trial vs paid vs enterprise (Talk with sales).
  freeTier: false, trialDays: 14, enterprise: false, contactSalesUrl: '', salesEmail: '',
  // Capacity model v2 — defaults match legacy behaviour (daily ingest cap,
  // runs-metered synthetics, no Vera plan gating, hard caps). Empty = unlimited.
  ingestCapMode: 'DAILY', maxEventsPerMonth: '', ingestOveragePricePerGb: '',
  syntheticsMeteringMode: 'RUNS', maxSyntheticChecks: '', maxSyntheticRunsPerMonth: '',
  veraQueriesPerMonth: '', veraOveragePricePerQuery: '', maxSentinelEvaluationsPerMonth: '',
  networkMeteringMode: 'RUNS', maxNetworkChecks: '', maxNetworkRunsPerMonth: '',
  networkOveragePricePerRun: '', maxSnmpDevices: '', snmpDeviceOveragePrice: '',
};

/** Format a byte count for compact display (B / KB / MB / GB / TB). */
function formatBytes(n) {
  const v = Number(n || 0);
  if (v < 0) return 'Unlimited';
  if (v < 1024) return `${v} B`;
  if (v < 1024 ** 2) return `${(v / 1024).toFixed(1)} KB`;
  if (v < 1024 ** 3) return `${(v / 1024 ** 2).toFixed(1)} MB`;
  if (v < 1024 ** 4) return `${(v / 1024 ** 3).toFixed(2)} GB`;
  return `${(v / 1024 ** 4).toFixed(2)} TB`;
}

/**
 * Operator page: manage subscription plans + assign them to tenants +
 * destructive ClickHouse wipes per tenant.
 *
 * Layout:
 *  - Plans table (CRUD, set default)
 *  - Tenants table (assign plan, wipe RUM data, wipe all data)
 */
export default function SubscriptionPlansPage() {
  const notification = useNotification();
  const [plans, setPlans] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(EMPTY_PLAN);
  const [editingId, setEditingId] = useState(null);

  const [confirmWipe, setConfirmWipe] = useState(null); // { orgId, orgSlug, scope, slugInput }

  // Retention-enforcer state — last run summary + in-progress flag.
  const [retention, setRetention] = useState(null);
  const [retentionRunning, setRetentionRunning] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const [p, o, r] = await Promise.all([
        plansApi.list(),
        plansApi.listOrgs(),
        plansApi.retentionStatus().catch(() => null),
      ]);
      setPlans(Array.isArray(p) ? p : []);
      setOrgs(Array.isArray(o) ? o : []);
      setRetention(r);
    } catch (e) {
      notification.error(`Failed to load: ${e.message || 'unknown'}`);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); }, []);

  const onRunRetention = async () => {
    setRetentionRunning(true);
    try {
      // Existing service returns { status, orgsProcessed, totalDeleted, errors }
      const result = await plansApi.runRetention();
      setRetention({
        inProgress: false,
        lastRun: {
          completedAt: new Date().toISOString(),
          trigger: 'manual',
          tenantsProcessed: result.orgsProcessed ?? 0,
          tenantsActuallyPurged: result.orgsProcessed ?? 0,
          statementsExecuted: result.totalDeleted ?? 0,
          statementsFailed: result.errors ?? 0,
          durationMs: 0,
        },
      });
      notification.success(
        `Retention done — ${result.orgsProcessed} orgs, ${result.totalDeleted} rows deleted` +
        (result.errors > 0 ? ` (${result.errors} errors)` : '')
      );
    } catch (e) {
      const detail = e?.response?.data?.message || e?.response?.data?.error || e?.message || 'unknown';
      notification.error(`Retention run failed: ${detail}`);
    } finally {
      setRetentionRunning(false);
    }
  };

  const planById = (id) => plans.find((p) => p.id === id);

  const onSavePlan = async () => {
    try {
      // Empty number fields must go to the API as null, not '' (the backend
      // Long/BigDecimal columns can't deserialize an empty string).
      const NUMERIC_NULLABLE = [
        'displayOrder', 'maxEventsPerMonth', 'ingestOveragePricePerGb',
        'maxSyntheticChecks', 'maxSyntheticRunsPerMonth',
        'veraQueriesPerMonth', 'veraOveragePricePerQuery', 'maxSentinelEvaluationsPerMonth',
        'maxNetworkChecks', 'maxNetworkRunsPerMonth', 'networkOveragePricePerRun',
        'maxSnmpDevices', 'snmpDeviceOveragePrice',
      ];
      const payload = { ...editing };
      NUMERIC_NULLABLE.forEach((k) => {
        if (payload[k] === '' || payload[k] === undefined) payload[k] = null;
      });
      if (editingId) {
        await plansApi.update(editingId, payload);
        notification.success(`Plan "${editing.code}" updated`);
      } else {
        await plansApi.create(payload);
        notification.success(`Plan "${editing.code}" created`);
      }
      setEditorOpen(false);
      refresh();
    } catch (e) {
      const detail = e?.response?.data?.error || e?.message || 'unknown';
      notification.error(`Save failed: ${detail}`);
    }
  };

  const onSetDefault = async (id) => {
    try {
      await plansApi.setDefault(id);
      notification.success('Default plan updated');
      refresh();
    } catch (e) {
      notification.error(`Failed: ${e.message || 'unknown'}`);
    }
  };

  const onDeletePlan = async (plan) => {
    if (!window.confirm(`Delete plan "${plan.code}"? This cannot be undone.`)) return;
    try {
      await plansApi.delete(plan.id);
      notification.success(`Plan "${plan.code}" deleted`);
      refresh();
    } catch (e) {
      const detail = e?.response?.data?.error || e?.message || 'unknown';
      notification.error(`Delete failed: ${detail}`);
    }
  };

  const onAssignPlan = async (orgId, planId) => {
    try {
      await plansApi.assignToOrg(planId, orgId);
      notification.success('Plan assigned');
      refresh();
    } catch (e) {
      notification.error(`Assign failed: ${e.message || 'unknown'}`);
    }
  };

  const onConfirmWipe = async () => {
    const { orgId, orgSlug, scope, slugInput } = confirmWipe;
    if (slugInput !== orgSlug) {
      notification.error('Slug confirmation mismatch — wipe aborted');
      return;
    }
    try {
      const fn = scope === 'rum' ? plansApi.wipeRumForOrg : plansApi.wipeAllForOrg;
      const res = await fn(orgId, orgSlug);
      notification.success(`Wiped ${scope.toUpperCase()} data for "${orgSlug}"`);
      console.log('Wipe result:', res);
      setConfirmWipe(null);
    } catch (e) {
      notification.error(`Wipe failed: ${e.message || 'unknown'}`);
    }
  };

  if (loading) {
    return <Box sx={{ p: 3 }}><CardSkeleton count={2} /></Box>;
  }

  return (
    <Box sx={{ maxWidth: 1280, mx: 'auto' }}>
      <Stack spacing={3}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h5" fontWeight={600}>Subscription Plans</Typography>
            <Typography variant="body2" color="text.secondary">
              Operator: define plans (quotas + retention + features) and assign them to tenants.
            </Typography>
          </Box>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => {
            setEditing(EMPTY_PLAN); setEditingId(null); setEditorOpen(true);
          }}>
            New Plan
          </Button>
        </Stack>

        <Card>
          <CardContent>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Code</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell align="right">Users</TableCell>
                  <TableCell align="right">Agents</TableCell>
                  <TableCell align="right">Data/day</TableCell>
                  <TableCell align="right">RUM TTL</TableCell>
                  <TableCell align="right">Logs TTL</TableCell>
                  <TableCell align="right">Traces TTL</TableCell>
                  <TableCell>Features</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {plans.map((p) => (
                  <TableRow key={p.id} hover>
                    <TableCell><code>{p.code}</code> {p.default && <Chip size="small" label="default" color="primary" />}</TableCell>
                    <TableCell>{p.name}</TableCell>
                    <TableCell align="right">{p.maxUsers}</TableCell>
                    <TableCell align="right">{p.maxAgents}</TableCell>
                    <TableCell align="right">{formatBytes(p.maxEventsPerDay)}</TableCell>
                    <TableCell align="right">{p.retentionRumDays}d</TableCell>
                    <TableCell align="right">{p.retentionLogsDays}d</TableCell>
                    <TableCell align="right">{p.retentionTracesDays}d</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        {p.sessionReplayEnabled && <Chip size="small" label="replay" />}
                        {p.advancedFunnelsEnabled && <Chip size="small" label="funnels" />}
                        {p.ssoEnabled && <Chip size="small" label="sso" />}
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title={p.default ? 'Default plan' : 'Set as default'}>
                        <IconButton size="small" onClick={() => onSetDefault(p.id)}>
                          {p.default ? <StarIcon fontSize="small" color="primary" /> : <StarBorderIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                      <IconButton size="small" onClick={() => {
                        setEditing({ ...p }); setEditingId(p.id); setEditorOpen(true);
                      }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => onDeletePlan(p)} disabled={p.default}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* ── Retention enforcement card ─────────────────────────────────── */}
        <Card>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap" useFlexGap>
              <AutoDeleteIcon color="action" />
              <Stack sx={{ flex: 1 }}>
                <Typography variant="subtitle1" fontWeight={600}>Retention enforcement</Typography>
                <Typography variant="caption" color="text.secondary">
                  Daily at 02:30 UTC. Each tenant's per-component retention setting
                  drives an <code>ALTER TABLE … DELETE WHERE org_id=X AND ts &lt; now() − INTERVAL N DAY</code>
                  per component (RUM events / sessions / replays, logs, traces, metrics).
                  Only orgs with an explicit ClickHouse cluster assignment are processed.
                </Typography>
              </Stack>
              <Button
                variant="contained"
                size="small"
                startIcon={retentionRunning ? <CircularProgress size={14} color="inherit" /> : <RefreshIcon />}
                disabled={retentionRunning || retention?.inProgress}
                onClick={onRunRetention}
              >
                {retention?.inProgress ? 'Running…' : 'Run retention now'}
              </Button>
            </Stack>
            {retention?.lastRun && (
              <Box sx={{
                mt: 2, p: 1.5, borderRadius: 1,
                bgcolor: (t) => t.palette.mode === 'dark' ? 'grey.900' : 'grey.100',
              }}>
                <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Last run</Typography>
                    <Typography variant="body2">{retention.lastRun.completedAt || '—'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Trigger</Typography>
                    <Typography variant="body2">{retention.lastRun.trigger || '—'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Orgs processed</Typography>
                    <Typography variant="body2">{retention.lastRun.tenantsProcessed}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Rows deleted</Typography>
                    <Typography variant="body2">
                      {retention.lastRun.statementsExecuted}
                      {retention.lastRun.statementsFailed > 0 && ` · ${retention.lastRun.statementsFailed} errors`}
                    </Typography>
                  </Box>
                </Stack>
                {retention.lastRun.error && (
                  <Alert severity="error" sx={{ mt: 1 }}>{retention.lastRun.error}</Alert>
                )}
              </Box>
            )}
          </CardContent>
        </Card>

        <Stack>
          <Typography variant="h6" fontWeight={600}>Tenants</Typography>
          <Typography variant="body2" color="text.secondary">
            Assign a plan to each tenant. Destructive wipes are gated by a slug-confirmation dialog.
          </Typography>
        </Stack>

        <Card>
          <CardContent>
            {orgs.length === 0 ? (
              <Alert severity="info">No organizations returned by the API. The /auth/organizations endpoint may not be wired up yet.</Alert>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Slug</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Current plan</TableCell>
                    <TableCell>Assign plan</TableCell>
                    <TableCell align="right">Destructive</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {orgs.map((o) => (
                    <TableRow key={o.id} hover>
                      <TableCell><code>{o.slug}</code></TableCell>
                      <TableCell>{o.name}</TableCell>
                      <TableCell>{planById(o.planId)?.name || o.plan || '—'}</TableCell>
                      <TableCell>
                        <FormControl size="small" sx={{ minWidth: 180 }}>
                          <Select
                            value={o.planId || ''}
                            displayEmpty
                            onChange={(e) => onAssignPlan(o.id, e.target.value)}
                          >
                            <MenuItem value="" disabled>Select plan…</MenuItem>
                            {plans.map((p) => (
                              <MenuItem key={p.id} value={p.id}>{p.name} ({p.code})</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Wipe all RUM data for this tenant">
                          <IconButton size="small" color="warning" onClick={() => setConfirmWipe({
                            orgId: o.id, orgSlug: o.slug, scope: 'rum', slugInput: '',
                          })}>
                            <DeleteForeverIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Wipe ALL telemetry data for this tenant (RUM + logs + traces + metrics)">
                          <IconButton size="small" color="error" onClick={() => setConfirmWipe({
                            orgId: o.id, orgSlug: o.slug, scope: 'all', slugInput: '',
                          })}>
                            <WarningIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </Stack>

      {/* ── Plan editor dialog ───────────────────────────────────────────── */}
      <Dialog open={editorOpen} onClose={() => setEditorOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingId ? `Edit plan "${editing.code}"` : 'New plan'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Stack direction="row" spacing={2}>
              <TextField label="Code (immutable)" value={editing.code}
                onChange={(e) => setEditing({ ...editing, code: e.target.value })}
                disabled={!!editingId} fullWidth size="small"
                helperText="lowercase identifier — e.g. starter, growth, custom-acme" />
              <TextField label="Name" value={editing.name}
                onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                fullWidth size="small" />
            </Stack>
            <TextField label="Description" value={editing.description || ''}
              onChange={(e) => setEditing({ ...editing, description: e.target.value })}
              multiline rows={2} fullWidth size="small" />

            <Divider><Typography variant="caption">Quotas</Typography></Divider>
            <Stack direction="row" spacing={2}>
              <TextField type="number" label="Max users" value={editing.maxUsers}
                onChange={(e) => setEditing({ ...editing, maxUsers: +e.target.value })} fullWidth size="small" />
              <TextField type="number" label="Max agents" value={editing.maxAgents}
                onChange={(e) => setEditing({ ...editing, maxAgents: +e.target.value })} fullWidth size="small" />
              <TextField type="number" label="Max tests" value={editing.maxTests}
                onChange={(e) => setEditing({ ...editing, maxTests: +e.target.value })} fullWidth size="small" />
            </Stack>
            <Stack direction="row" spacing={2}>
              {/* DB column is bytes; we expose MB to operators because typing
                  104857600 for "100 MB" is hostile. -1 stays special-cased
                  for "Unlimited" — operator types -1 in the field. */}
              <TextField
                type="number"
                label="Data per day (MB)"
                value={editing.maxEventsPerDay === -1 ? -1 : Math.round((editing.maxEventsPerDay || 0) / (1024 * 1024))}
                onChange={(e) => {
                  const n = +e.target.value;
                  setEditing({
                    ...editing,
                    maxEventsPerDay: n === -1 ? -1 : Math.max(0, n) * 1024 * 1024,
                  });
                }}
                helperText={
                  editing.maxEventsPerDay === -1
                    ? 'Unlimited'
                    : `Stored as ${formatBytes(editing.maxEventsPerDay || 0)}. Use -1 for unlimited.`
                }
                fullWidth
                size="small"
              />
              <TextField type="number" label="Max RUM apps" value={editing.maxRumApps}
                onChange={(e) => setEditing({ ...editing, maxRumApps: +e.target.value })} fullWidth size="small" />
            </Stack>

            <Divider><Typography variant="caption">Ingestion cap</Typography></Divider>
            <Stack direction="row" spacing={2}>
              <TextField select label="Cap window" value={editing.ingestCapMode || 'DAILY'}
                onChange={(e) => setEditing({ ...editing, ingestCapMode: e.target.value })}
                fullWidth size="small"
                helperText={editing.ingestCapMode === 'MONTHLY'
                  ? 'Uses "Data per month" below' : 'Uses "Data per day" above'}>
                <MenuItem value="DAILY">Daily</MenuItem>
                <MenuItem value="MONTHLY">Monthly</MenuItem>
              </TextField>
              {editing.ingestCapMode === 'MONTHLY' && (
                <TextField
                  type="number"
                  label="Data per month (GB)"
                  value={editing.maxEventsPerMonth === '' || editing.maxEventsPerMonth == null
                    ? ''
                    : (Number(editing.maxEventsPerMonth) === -1
                      ? -1
                      : Math.round(Number(editing.maxEventsPerMonth) / (1024 * 1024 * 1024)))}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === '') { setEditing({ ...editing, maxEventsPerMonth: '' }); return; }
                    const n = +v;
                    setEditing({
                      ...editing,
                      maxEventsPerMonth: n === -1 ? -1 : Math.max(0, n) * 1024 * 1024 * 1024,
                    });
                  }}
                  helperText="Blank / -1 = unlimited. Stored as bytes."
                  fullWidth size="small"
                />
              )}
              <TextField type="number" label="Ingest overage ($/GB)"
                value={editing.ingestOveragePricePerGb ?? ''}
                onChange={(e) => setEditing({ ...editing, ingestOveragePricePerGb: e.target.value })}
                helperText="Blank = hard cap. Set = allow overflow, bill per extra GB."
                fullWidth size="small" />
            </Stack>

            <Divider><Typography variant="caption">Retention (days)</Typography></Divider>
            <Stack direction="row" spacing={2}>
              <TextField type="number" label="RUM" value={editing.retentionRumDays}
                onChange={(e) => setEditing({ ...editing, retentionRumDays: +e.target.value })} fullWidth size="small" />
              <TextField type="number" label="Logs" value={editing.retentionLogsDays}
                onChange={(e) => setEditing({ ...editing, retentionLogsDays: +e.target.value })} fullWidth size="small" />
              <TextField type="number" label="Traces" value={editing.retentionTracesDays}
                onChange={(e) => setEditing({ ...editing, retentionTracesDays: +e.target.value })} fullWidth size="small" />
              <TextField type="number" label="Metrics" value={editing.retentionMetricsDays}
                onChange={(e) => setEditing({ ...editing, retentionMetricsDays: +e.target.value })} fullWidth size="small" />
            </Stack>

            <Divider><Typography variant="caption">Synthetics metering</Typography></Divider>
            <Stack direction="row" spacing={2}>
              <TextField select label="Meter by" value={editing.syntheticsMeteringMode || 'RUNS'}
                onChange={(e) => setEditing({ ...editing, syntheticsMeteringMode: e.target.value })}
                fullWidth size="small">
                <MenuItem value="RUNS">Runs per month</MenuItem>
                <MenuItem value="CHECKS">Configured checks</MenuItem>
              </TextField>
              {editing.syntheticsMeteringMode === 'CHECKS' ? (
                <TextField type="number" label="Max synthetic checks"
                  value={editing.maxSyntheticChecks ?? ''}
                  onChange={(e) => setEditing({ ...editing, maxSyntheticChecks: e.target.value })}
                  helperText="Blank = unlimited." fullWidth size="small" />
              ) : (
                <TextField type="number" label="Max runs / month"
                  value={editing.maxSyntheticRunsPerMonth ?? ''}
                  onChange={(e) => setEditing({ ...editing, maxSyntheticRunsPerMonth: e.target.value })}
                  helperText="Blank = legacy tier default." fullWidth size="small" />
              )}
            </Stack>

            <Divider><Typography variant="caption">AI (Vera &amp; Sentinel)</Typography></Divider>
            <Stack direction="row" spacing={2}>
              <TextField type="number" label="Vera queries / month"
                value={editing.veraQueriesPerMonth ?? ''}
                onChange={(e) => setEditing({ ...editing, veraQueriesPerMonth: e.target.value })}
                helperText="Blank = not plan-gated." fullWidth size="small" />
              <TextField type="number" label="Vera overage ($/query)"
                value={editing.veraOveragePricePerQuery ?? ''}
                onChange={(e) => setEditing({ ...editing, veraOveragePricePerQuery: e.target.value })}
                helperText="Blank = hard cap. Set = allow overflow, bill per extra query."
                fullWidth size="small" />
              <TextField type="number" label="Sentinel evaluations / month"
                value={editing.maxSentinelEvaluationsPerMonth ?? ''}
                onChange={(e) => setEditing({ ...editing, maxSentinelEvaluationsPerMonth: e.target.value })}
                helperText="Blank = unlimited." fullWidth size="small" />
            </Stack>

            <Divider><Typography variant="caption">Network observability</Typography></Divider>
            <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
              Active checks are metered like synthetics (by configured checks or by monthly runs).
              SNMP devices use the per-monitored-device model. Mesh, topology and discovery are bundled.
            </Typography>
            <Stack direction="row" spacing={2}>
              <TextField select label="Meter checks by" value={editing.networkMeteringMode || 'RUNS'}
                onChange={(e) => setEditing({ ...editing, networkMeteringMode: e.target.value })}
                fullWidth size="small">
                <MenuItem value="RUNS">Runs per month</MenuItem>
                <MenuItem value="CHECKS">Configured checks</MenuItem>
              </TextField>
              {editing.networkMeteringMode === 'CHECKS' ? (
                <TextField type="number" label="Max network checks"
                  value={editing.maxNetworkChecks ?? ''}
                  onChange={(e) => setEditing({ ...editing, maxNetworkChecks: e.target.value })}
                  helperText="Blank = unlimited." fullWidth size="small" />
              ) : (
                <TextField type="number" label="Max runs / month"
                  value={editing.maxNetworkRunsPerMonth ?? ''}
                  onChange={(e) => setEditing({ ...editing, maxNetworkRunsPerMonth: e.target.value })}
                  helperText="Blank = unlimited." fullWidth size="small" />
              )}
              {editing.networkMeteringMode !== 'CHECKS' && (
                <TextField type="number" label="Run overage ($/run)"
                  value={editing.networkOveragePricePerRun ?? ''}
                  onChange={(e) => setEditing({ ...editing, networkOveragePricePerRun: e.target.value })}
                  helperText="Blank = hard cap. Set = allow overflow, bill per extra run."
                  fullWidth size="small" />
              )}
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField type="number" label="Max SNMP devices"
                value={editing.maxSnmpDevices ?? ''}
                onChange={(e) => setEditing({ ...editing, maxSnmpDevices: e.target.value })}
                helperText="Blank = unlimited." fullWidth size="small" />
              <TextField type="number" label="SNMP device overage ($/device)"
                value={editing.snmpDeviceOveragePrice ?? ''}
                onChange={(e) => setEditing({ ...editing, snmpDeviceOveragePrice: e.target.value })}
                helperText="Blank = hard cap. Set = allow overflow, bill per extra device."
                fullWidth size="small" />
            </Stack>

            <Divider><Typography variant="caption">Features</Typography></Divider>
            <Stack direction="row" spacing={2} flexWrap="wrap">
              <FormControlLabel control={<Switch checked={editing.sessionReplayEnabled}
                onChange={(e) => setEditing({ ...editing, sessionReplayEnabled: e.target.checked })} />}
                label="Session replay" />
              <FormControlLabel control={<Switch checked={editing.advancedFunnelsEnabled}
                onChange={(e) => setEditing({ ...editing, advancedFunnelsEnabled: e.target.checked })} />}
                label="Advanced funnels" />
              <FormControlLabel control={<Switch checked={editing.ssoEnabled}
                onChange={(e) => setEditing({ ...editing, ssoEnabled: e.target.checked })} />}
                label="SSO" />
              <FormControlLabel control={<Switch checked={editing.auditLogEnabled}
                onChange={(e) => setEditing({ ...editing, auditLogEnabled: e.target.checked })} />}
                label="Audit log" />
            </Stack>

            <Divider>
              <Typography variant="caption">Public signup</Typography>
            </Divider>
            <Stack direction="row" spacing={2} flexWrap="wrap" alignItems="center">
              <FormControlLabel
                control={
                  <Switch
                    checked={!!editing.signupEnabled}
                    onChange={(e) => setEditing({ ...editing, signupEnabled: e.target.checked })}
                    color="success"
                  />
                }
                label={
                  <span>
                    Available for signup
                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                      Listed on the public /signup page (master switch is in Stripe settings)
                    </Typography>
                  </span>
                }
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={!!editing.freeTier}
                    onChange={(e) => setEditing({ ...editing, freeTier: e.target.checked, enterprise: e.target.checked ? false : editing.enterprise })}
                    color="success"
                  />
                }
                label={
                  <span>
                    Free tier (trial)
                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                      Starts a trial, no Stripe
                    </Typography>
                  </span>
                }
              />
              {editing.freeTier && (
                <TextField
                  type="number"
                  label="Trial days"
                  value={editing.trialDays ?? 14}
                  onChange={(e) => setEditing({ ...editing, trialDays: Number(e.target.value) || 14 })}
                  size="small"
                  sx={{ width: 120 }}
                  InputProps={{ inputProps: { min: 1, max: 365 } }}
                />
              )}
              <FormControlLabel
                control={
                  <Switch
                    checked={!!editing.enterprise}
                    onChange={(e) => setEditing({ ...editing, enterprise: e.target.checked, freeTier: e.target.checked ? false : editing.freeTier })}
                    color="primary"
                  />
                }
                label={
                  <span>
                    Enterprise (Talk with sales)
                    <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                      No self-serve checkout
                    </Typography>
                  </span>
                }
              />
            </Stack>
            {editing.enterprise && (
              <Stack direction="row" spacing={2}>
                <TextField
                  label="Contact sales URL (optional)"
                  placeholder="https://verops.io/contact-sales — leave blank to use the built-in form"
                  value={editing.contactSalesUrl || ''}
                  onChange={(e) => setEditing({ ...editing, contactSalesUrl: e.target.value })}
                  size="small"
                  fullWidth
                  helperText="Blank → the 'Contact sales' button opens the built-in inquiry popup"
                />
                <TextField
                  label="Sales email (targeted)"
                  placeholder="sales@yourcompany.com"
                  value={editing.salesEmail || ''}
                  onChange={(e) => setEditing({ ...editing, salesEmail: e.target.value })}
                  size="small"
                  fullWidth
                  helperText="Inquiries from this plan's form are emailed here"
                />
              </Stack>
            )}
            <Stack direction="row" spacing={2}>
              <TextField
                label="Display price"
                placeholder="e.g. $49/month or Free forever"
                value={editing.displayPrice || ''}
                onChange={(e) => setEditing({ ...editing, displayPrice: e.target.value })}
                size="small"
                fullWidth
                helperText="Free-form text shown on the signup card"
              />
              <TextField
                type="number"
                label="Display order"
                value={editing.displayOrder ?? ''}
                onChange={(e) => setEditing({ ...editing, displayOrder: e.target.value === '' ? null : Number(e.target.value) })}
                size="small"
                sx={{ width: 160 }}
                helperText="Lower first"
              />
            </Stack>
            <Stack direction="row" spacing={2}>
              <TextField
                label="Stripe Price ID"
                placeholder="price_..."
                value={editing.stripePriceId || ''}
                onChange={(e) => setEditing({ ...editing, stripePriceId: e.target.value })}
                size="small"
                fullWidth
                helperText="Required for paid plans — paste from Stripe dashboard. Leave blank for free plans."
              />
              <TextField
                label="Stripe Product ID"
                placeholder="prod_..."
                value={editing.stripeProductId || ''}
                onChange={(e) => setEditing({ ...editing, stripeProductId: e.target.value })}
                size="small"
                fullWidth
                helperText="Optional — informational only"
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditorOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={onSavePlan}>{editingId ? 'Save' : 'Create'}</Button>
        </DialogActions>
      </Dialog>

      {/* ── Wipe confirmation dialog ─────────────────────────────────────── */}
      <Dialog open={!!confirmWipe} onClose={() => setConfirmWipe(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: 'error.main' }}>
          <WarningIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          Confirm destructive wipe
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Alert severity="error">
              You are about to <strong>permanently delete</strong>{' '}
              <strong>{confirmWipe?.scope === 'rum' ? 'all RUM data' : 'ALL telemetry data (RUM + logs + traces + metrics)'}</strong>{' '}
              for tenant <code>{confirmWipe?.orgSlug}</code>. Tables remain so writes can resume immediately.
            </Alert>
            <TextField
              label={`Type the slug "${confirmWipe?.orgSlug}" to confirm`}
              value={confirmWipe?.slugInput || ''}
              onChange={(e) => setConfirmWipe({ ...confirmWipe, slugInput: e.target.value })}
              fullWidth autoFocus size="small"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmWipe(null)}>Cancel</Button>
          <Button color="error" variant="contained"
            disabled={confirmWipe?.slugInput !== confirmWipe?.orgSlug}
            onClick={onConfirmWipe}>
            Wipe data
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
